import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Eye } from "lucide-react";
import { useMoneyAccounts } from "@/api/cash";
import { useClients } from "@/api/clients";
import { invoiceKeys, listInvoices, type InvoiceFilters, type PdfFile } from "@/api/invoices";
import { listPayments, paymentKeys, type PaymentFilters } from "@/api/payments";
import {
  fetchReceiptPdf,
  previewCollection,
  previewReceipt,
  receiptFileName,
  useCollectReceipt,
  useIssueReceipt,
  type CollectionInput,
  type Receipt,
  type ReceiptInput,
} from "@/api/receipts";
import { ArrivalFields, convertedAmount, paymentInput, type PaymentValues } from "@/components/forms/PaymentFields";
import { RupeeValue } from "@/components/forms/RupeeValue";
import { cents, InvoicePicker, inReceiptOrder, type InvoiceLine } from "@/components/receipts/InvoicePicker";
import { PaymentPicker, type PaymentLine } from "@/components/receipts/PaymentPicker";
import { RequestError } from "@/components/RequestError";
import { Button, buttonClass } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SelectField, TextField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page-header";
import { todayIso } from "@/lib/dates";
import { saveBlob } from "@/lib/files";
import { formatMoney } from "@/lib/format";
import { centsToAmount } from "@/lib/money";
import { shell } from "@/lib/platform";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type Mode = "collect" | "recorded";

const MODES: { value: Mode; label: string; description: string }[] = [
  {
    value: "collect",
    label: "Money received now, for several invoices",
    description:
      "Tick the invoices the money settles - calls, whole voyages or several voyages. A payment is recorded on each and one receipt issued for them all.",
  },
  {
    value: "recorded",
    label: "Payments already recorded",
    description: "Tick payments recorded earlier that no receipt acknowledges yet.",
  },
];

function blankPayment(): PaymentValues {
  return {
    received_on: todayIso(),
    amount: "",
    bank_charges: "0.00",
    method: "bank_transfer",
    received_currency: "",
    received_amount: "",
    received_rate: "",
    received_by: "",
    received_place: "",
    reference: "",
    received_into_id: "",
    inr_credited: "",
    fx_rate: "",
    fira_ref: "",
    purpose_code: "",
    notes: "",
  };
}

// What the API takes in one receipt (ReceiptIn / CollectionIn lines).
const MAX_LINES = 20;

/** A write-off that is typed but is not an amount: refused rather than silently dropped. */
function badWriteOff(value: string): boolean {
  return value.trim() !== "" && !(cents(value) >= 0);
}

/** "1850" -> "1850.00", with a write-off only when there is one. */
function line<T extends object>(base: T, writeOff: string): T & { write_off?: string } {
  return cents(writeOff) > 0 ? { ...base, write_off: centsToAmount(cents(writeOff)) } : base;
}

/** A PDF on the page; Android's WebView cannot show one, so there it is handed to a viewer. */
function PdfFrame({ title, url, file }: { title: string; url: string; file: PdfFile }) {
  if (shell() === "android") {
    return (
      <Button size="sm" onClick={() => void saveBlob(file.blob, file.filename)}>
        <Eye className="h-4 w-4" aria-hidden />
        Open {title}
      </Button>
    );
  }
  return <iframe title={title} src={url} className="h-[75vh] w-full rounded-md border bg-white" />;
}

function IssuedReceipt({ receipt, onAnother }: { receipt: Receipt; onAnother: () => void }) {
  const [shown, setShown] = useState<{ file: PdfFile; url: string } | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    let url = "";
    fetchReceiptPdf(receipt.id, receipt.documents[0]?.version ?? 1, receiptFileName(receipt))
      .then((file) => {
        if (!live) return;
        url = URL.createObjectURL(file.blob);
        setShown({ file, url });
      })
      .catch((err: unknown) => live && setError(err));
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [receipt]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Receipt ${receipt.receipt_no} issued`}
        description={`${formatMoney(receipt.amount, receipt.currency)}${
          receipt.received_currency && receipt.received_amount
            ? ` (${formatMoney(receipt.received_amount, receipt.received_currency)})`
            : ""
        } from ${receipt.client_name}.`}
        actions={
          <>
            {shown && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void saveBlob(shown.file.blob, shown.file.filename)}
              >
                <Download className="h-3.5 w-3.5" aria-hidden />
                Download
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onAnother}>
              Another receipt
            </Button>
            <Link to="/receipts" className={buttonClass("primary", "sm")}>
              All receipts
            </Link>
          </>
        }
      />
      <RequestError error={error} />
      {shown && <PdfFrame title={receipt.receipt_no} url={shown.url} file={shown.file} />}
    </div>
  );
}

/**
 * One receipt for several invoices, payments, vessels or voyages. Pick the client, tick what
 * the receipt acknowledges, enter the money once, preview the actual PDF (every field stays
 * editable beside it) and only then issue it.
 */
export function NewReceiptPage() {
  const id = useId();
  const clients = useClients("", false);
  const accounts = useMoneyAccounts();
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState<Mode>("collect");
  const [invoiceLines, setInvoiceLines] = useState<Record<string, InvoiceLine>>({});
  const [paymentLines, setPaymentLines] = useState<Record<string, PaymentLine>>({});
  const [receiptDate, setReceiptDate] = useState("");
  const [amountTyped, setAmountTyped] = useState(false);
  const { values, set, setValues } = useFormValues<PaymentValues>(blankPayment());
  const [preview, setPreview] = useState<{ key: string; url: string; file: PdfFile } | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [previewing, setPreviewing] = useState(false);
  // Only the latest request may show its PDF; one that lands after a change is dropped.
  const previewRequest = useRef(0);
  const [issued, setIssued] = useState<Receipt | null>(null);
  const confirming = useSubmitState();
  const issueReceipt = useIssueReceipt();
  const collectReceipt = useCollectReceipt();

  const invoiceFilters: InvoiceFilters = { clientId, status: "unpaid", limit: 200 };
  const invoices = useQuery({
    queryKey: invoiceKeys.list(invoiceFilters),
    queryFn: () => listInvoices(invoiceFilters),
    enabled: clientId !== "" && mode === "collect",
  });
  const paymentFilters: PaymentFilters = { clientId, limit: 500 };
  const payments = useQuery({
    queryKey: paymentKeys.register(paymentFilters),
    queryFn: () => listPayments(paymentFilters),
    enabled: clientId !== "" && mode === "recorded",
  });

  const defaultIn = accounts.data?.find((account) => account.is_default_in)?.id;
  useEffect(() => {
    if (defaultIn) setValues((current) => (current.received_into_id ? current : { ...current, received_into_id: defaultIn }));
  }, [defaultIn, setValues]);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview.url)), [preview]);

  const client = clients.data?.find((row) => row.id === clientId);
  const open = inReceiptOrder(
    (invoices.data ?? []).filter(
      (row) =>
        row.client_id === clientId &&
        row.doc_type === "invoice" &&
        row.state === "issued" &&
        row.closed_at === null &&
        Number(row.balance) > 0,
    ),
  );
  const chosen = open.filter((invoice) => invoice.id in invoiceLines);
  const waiting = (payments.data ?? []).filter((row) => !row.receipt_id && !row.voided_at);
  const chosenPayments = waiting.filter((row) => row.id in paymentLines);
  const currency =
    (mode === "collect" ? chosen[0]?.currency : chosenPayments[0]?.currency) ??
    client?.currency ??
    "USD";

  // What the invoices take; the amount follows it (or the riyals at the rate) until typed.
  const takenCents = chosen.reduce((sum, invoice) => sum + cents(invoiceLines[invoice.id]?.amount ?? ""), 0);
  const converted = convertedAmount(values);
  const amount = amountTyped
    ? values.amount
    : (converted ?? (takenCents > 0 ? centsToAmount(takenCents) : ""));
  const amountCents = cents(amount);
  const shortCents = takenCents - amountCents;
  const last = chosen.at(-1);
  const lastLine = last ? invoiceLines[last.id] : undefined;
  const netCents = amountCents - cents(values.bank_charges);

  const collection: CollectionInput = {
    payment: { ...paymentInput(values), amount: centsToAmount(amountCents) },
    lines: chosen.map((invoice) => {
      const taken = invoiceLines[invoice.id] ?? { amount: "", write_off: "" };
      return line({ invoice_id: invoice.id, amount: centsToAmount(cents(taken.amount)) }, taken.write_off);
    }),
    receipt_date: receiptDate || null,
  };
  const acknowledgement: ReceiptInput = {
    lines: chosenPayments.map((payment) =>
      line({ payment_id: payment.id }, paymentLines[payment.id]?.write_off ?? ""),
    ),
    receipt_date: receiptDate || null,
  };
  const lineCount = mode === "collect" ? chosen.length : chosenPayments.length;
  const writeOffs =
    mode === "collect"
      ? chosen.map((invoice) => invoiceLines[invoice.id]?.write_off ?? "")
      : chosenPayments.map((payment) => paymentLines[payment.id]?.write_off ?? "");
  const problem =
    lineCount > MAX_LINES
      ? `One receipt takes at most ${MAX_LINES} lines; ${lineCount} are ticked.`
      : writeOffs.some(badWriteOff)
        ? "A write-off is not an amount; correct it or leave it empty."
        : null;
  const ready =
    problem === null &&
    (mode === "collect"
      ? chosen.length > 0 && amountCents > 0 && shortCents === 0 && values.received_on !== ""
      : chosenPayments.length > 0);
  const key = JSON.stringify(mode === "collect" ? [mode, collection] : [mode, acknowledgement]);
  const current = preview !== null && preview.key === key;

  function reset(): void {
    previewRequest.current += 1;
    setInvoiceLines({});
    setPaymentLines({});
    setAmountTyped(false);
    setPreview(null);
    setPreviewError(null);
  }

  function chooseClient(next: string): void {
    setClientId(next);
    // Another client's money: nothing of the last one's rate, riyals or reference carries over.
    setValues((current) => ({ ...blankPayment(), received_into_id: current.received_into_id }));
    setReceiptDate("");
    reset();
  }

  function writeOffShortfall(): void {
    if (!last || !lastLine) return;
    setInvoiceLines({
      ...invoiceLines,
      [last.id]: {
        amount: centsToAmount(cents(lastLine.amount) - shortCents),
        write_off: centsToAmount(cents(lastLine.write_off) + shortCents),
      },
    });
  }

  async function showPreview(): Promise<void> {
    const request = ++previewRequest.current;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const file =
        mode === "collect" ? await previewCollection(collection) : await previewReceipt(acknowledgement);
      if (request !== previewRequest.current) return;
      setPreview({ key, url: URL.createObjectURL(file.blob), file });
    } catch (err) {
      setPreviewError(err);
    } finally {
      setPreviewing(false);
    }
  }

  function confirm(): void {
    void confirming.run(async () => {
      setIssued(
        mode === "collect"
          ? await collectReceipt.mutateAsync(collection)
          : await issueReceipt.mutateAsync(acknowledgement),
      );
    });
  }

  if (issued) {
    return (
      <IssuedReceipt
        receipt={issued}
        onAnother={() => {
          setIssued(null);
          setValues(blankPayment());
          reset();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="New receipt"
        description="One receipt for money that settles several invoices, vessels or voyages, or for payments already recorded."
        actions={
          <Link to="/receipts" className={buttonClass("outline", "sm")}>
            Back to receipts
          </Link>
        }
      />
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Who paid, and for what</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <SelectField
                id={`${id}-client`}
                label="Client"
                value={clientId}
                onChange={chooseClient}
                options={(clients.data ?? []).map((row) => ({
                  value: row.id,
                  label: `${row.code} · ${row.legal_name}`,
                }))}
                placeholder="Choose the client"
              />
              <RequestError error={clients.error} />
              <fieldset className="flex flex-col gap-3">
                <legend className="mb-1 text-sm font-medium">What does this receipt acknowledge?</legend>
                {MODES.map((option) => (
                  <div key={option.value} className="flex flex-col gap-0.5">
                    <label htmlFor={`${id}-mode-${option.value}`} className="flex items-center gap-2 text-sm">
                      <input
                        id={`${id}-mode-${option.value}`}
                        type="radio"
                        name={`${id}-mode`}
                        checked={mode === option.value}
                        onChange={() => {
                          setMode(option.value);
                          setPreview(null);
                          setPreviewError(null);
                        }}
                        aria-describedby={`${id}-mode-${option.value}-hint`}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                      />
                      {option.label}
                    </label>
                    <p id={`${id}-mode-${option.value}-hint`} className="ml-6 text-xs text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                ))}
              </fieldset>
            </CardContent>
          </Card>

          {clientId && (
            <Card>
              <CardHeader>
                <CardTitle>{mode === "collect" ? "2. The invoices it settles" : "2. The payments"}</CardTitle>
                <CardDescription>
                  {mode === "collect"
                    ? "Each takes its balance; change an amount for a part payment, or write off a shortfall you waive."
                    : "Payments must have arrived the same way and in the same currency to share a receipt."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {mode === "collect" ? (
                  <>
                    <RequestError error={invoices.error} />
                    {invoices.isPending ? (
                      <p className="text-sm text-muted-foreground">Loading open invoices…</p>
                    ) : (
                      <InvoicePicker id={`${id}-invoices`} invoices={open} lines={invoiceLines} onChange={setInvoiceLines} />
                    )}
                  </>
                ) : (
                  <>
                    <RequestError error={payments.error} />
                    {payments.isPending ? (
                      <p className="text-sm text-muted-foreground">Loading payments…</p>
                    ) : (
                      <PaymentPicker id={`${id}-payments`} payments={waiting} lines={paymentLines} onChange={setPaymentLines} />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {clientId && mode === "collect" && (
            <Card>
              <CardHeader>
                <CardTitle>3. The money</CardTitle>
                <CardDescription>Entered once for all the invoices ticked.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <TextField
                    id={`${id}-received-on`}
                    label="Received on"
                    type="date"
                    value={values.received_on}
                    onChange={(value) => set("received_on", value)}
                  />
                  <TextField
                    id={`${id}-amount`}
                    label={`Amount (${currency})`}
                    inputMode="decimal"
                    value={amount}
                    onChange={(value) => {
                      setAmountTyped(true);
                      set("amount", value);
                    }}
                    hint={amountTyped ? "As typed" : "Follows the invoices, or the riyals at the rate"}
                  />
                  <TextField
                    id={`${id}-charges`}
                    label={`Bank charges deducted (${currency})`}
                    inputMode="decimal"
                    value={values.bank_charges}
                    onChange={(value) => set("bank_charges", value)}
                    hint="Shared out over the invoices with the money"
                  />
                </div>
                {chosen.length > 0 && amountCents > 0 && (
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {shortCents === 0 ? (
                      <span>
                        The invoices take <strong>{formatMoney(amount, currency)}</strong>: it adds up.
                      </span>
                    ) : (
                      <span className="text-destructive">
                        {`The invoices take ${formatMoney(centsToAmount(takenCents), currency)}, not the ${formatMoney(centsToAmount(amountCents), currency)} received.`}
                      </span>
                    )}
                    {last && lastLine && shortCents > 0 && shortCents < cents(lastLine.amount) && (
                      <Button size="sm" variant="outline" onClick={writeOffShortfall}>
                        {`Write off ${formatMoney(centsToAmount(shortCents), currency)} on ${last.invoice_no ?? "the last invoice"}`}
                      </Button>
                    )}
                  </div>
                )}
                <ArrivalFields id={id} currency={currency} values={values} set={set} errors={{}} wholeSum />
                {currency !== "INR" && (
                  <RupeeValue
                    key={clientId}
                    id={`${id}-rupees`}
                    currency={currency}
                    net={netCents > 0 ? centsToAmount(netCents) : null}
                    receivedOn={values.received_on}
                    receivedCurrency={values.received_currency}
                    receivedRate={values.received_rate}
                    fxRate={values.fx_rate}
                    inrCredited={values.inr_credited}
                    onChange={(rates) => setValues((now) => ({ ...now, ...rates }))}
                  />
                )}
                <fieldset className="flex flex-col gap-3">
                  <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Bank credit (can be added later from the eFIRA)
                  </legend>
                  <div className="grid gap-4 md:grid-cols-2">
                    {accounts.data && (
                      <SelectField
                        id={`${id}-into`}
                        label="Received into"
                        value={values.received_into_id}
                        onChange={(value) => set("received_into_id", value)}
                        options={accounts.data
                          .filter((account) => !account.archived_at)
                          .map((account) => ({ value: account.id, label: `${account.name} (${account.currency})` }))}
                        placeholder="Not specified"
                      />
                    )}
                    <TextField
                      id={`${id}-reference`}
                      label="Bank reference"
                      value={values.reference}
                      onChange={(value) => set("reference", value)}
                      hint="SWIFT / UTR reference, printed on the receipt"
                    />
                    <TextField
                      id={`${id}-inr`}
                      label="INR credited"
                      inputMode="decimal"
                      value={values.inr_credited}
                      onChange={(value) => set("inr_credited", value)}
                      hint="What the bank credited, as on the eFIRA; optional"
                    />
                    <TextField
                      id={`${id}-fira`}
                      label="eFIRA / FIRC number"
                      value={values.fira_ref}
                      onChange={(value) => set("fira_ref", value)}
                    />
                  </div>
                </fieldset>
              </CardContent>
            </Card>
          )}

          {clientId && (
            <TextField
              id={`${id}-receipt-date`}
              label="Receipt date"
              type="date"
              value={receiptDate}
              onChange={setReceiptDate}
              hint="Leave empty for today; it cannot be before the money arrived"
              className="sm:max-w-xs"
            />
          )}
        </div>

        <div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          <Card>
            <CardHeader>
              <CardTitle>Preview and issue</CardTitle>
              <CardDescription>
                The receipt exactly as it will be issued, with PREVIEW in place of its number.
                Correct anything on the left, update the preview, and issue it only when it is
                right. Nothing is saved before that.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => void showPreview()}
                  disabled={!ready || previewing || current}
                >
                  <Eye className="h-4 w-4" aria-hidden />
                  {previewing ? "Preparing…" : preview ? "Update preview" : "Preview receipt"}
                </Button>
                <Button onClick={confirm} disabled={!ready || !current || previewing || confirming.saving}>
                  {confirming.saving ? "Issuing…" : "Confirm and issue receipt"}
                </Button>
              </div>
              {problem && <p className="text-sm text-destructive">{problem}</p>}
              {preview && !current && (
                <p className="text-sm text-amber-700">
                  The fields have changed since the preview - update it before issuing.
                </p>
              )}
              <RequestError error={previewError} />
              <RequestError error={confirming.error} />
              {preview && <PdfFrame title="Receipt preview" url={preview.url} file={preview.file} />}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
